import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { DumbbellIcon, ScaleIcon, ZapIcon, HeartIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, CheckIcon, ShoppingCartIcon } from 'lucide-react';
type Goal = 'muscle' | 'weight' | 'performance' | 'health';
interface Supplement {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  goals: Goal[];
}
const mockSupplements: Supplement[] = [{
  id: '1',
  name: '100% Whey Protein Isolate',
  description: 'Fast-absorbing protein for muscle recovery',
  price: 249,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['muscle', 'performance']
}, {
  id: '2',
  name: 'Creatine Monohydrate',
  description: 'Increase strength and power output',
  price: 129,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['muscle', 'performance']
}, {
  id: '3',
  name: 'Thermogenic Fat Burner',
  description: 'Boost metabolism and energy',
  price: 189,
  image: 'https://drnutrition.com/storage/media/uksBiPpW7gAOFEDeDTmu50CbihxIWLQKjiOeSqtu.jpg',
  goals: ['weight']
}, {
  id: '4',
  name: 'L-Carnitine Liquid',
  description: 'Convert fat into energy',
  price: 149,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['weight', 'performance']
}, {
  id: '5',
  name: 'Pre-Workout Explosive',
  description: 'Intense energy and focus',
  price: 199,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['performance', 'muscle']
}, {
  id: '6',
  name: 'Daily Multivitamin',
  description: 'Essential vitamins and minerals',
  price: 99,
  image: 'https://drnutrition.com/storage/media/uksBiPpW7gAOFEDeDTmu50CbihxIWLQKjiOeSqtu.jpg',
  goals: ['health', 'muscle', 'weight', 'performance']
}, {
  id: '7',
  name: 'Omega-3 Fish Oil',
  description: 'Heart and joint health support',
  price: 119,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['health', 'weight']
}, {
  id: '8',
  name: 'BCAA Recovery',
  description: 'Intra-workout muscle preservation',
  price: 159,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['muscle', 'performance']
}];
export function BundleBuilderPage() {
  const [step, setStep] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [selectedSupplements, setSelectedSupplements] = useState<string[]>([]);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  const goals = [{
    id: 'muscle' as Goal,
    title: 'Muscle Building',
    description: 'Gain size and strength',
    icon: <DumbbellIcon className="w-8 h-8" data-id="element-6489" data-mp="3hrex7vkwtgbe" />,
    emoji: '💪'
  }, {
    id: 'weight' as Goal,
    title: 'Weight Loss',
    description: 'Burn fat and get lean',
    icon: <ScaleIcon className="w-8 h-8" data-id="element-6490" data-mp="1p2xnrtuu0z4x" />,
    emoji: '🔥'
  }, {
    id: 'performance' as Goal,
    title: 'Performance',
    description: 'Boost energy and endurance',
    icon: <ZapIcon className="w-8 h-8" data-id="element-6491" data-mp="1g30ufexhzc3j" />,
    emoji: '⚡'
  }, {
    id: 'health' as Goal,
    title: 'General Health',
    description: 'Overall wellness and immunity',
    icon: <HeartIcon className="w-8 h-8" data-id="element-6492" data-mp="27tylyfo4oqnf" />,
    emoji: '❤️'
  }];
  const filteredSupplements = mockSupplements.filter(s => selectedGoal ? s.goals.includes(selectedGoal) : true);
  const toggleSupplement = (id: string) => {
    setSelectedSupplements(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  const originalTotal = selectedSupplements.reduce((total, id) => {
    const supp = mockSupplements.find(s => s.id === id);
    return total + (supp?.price || 0);
  }, 0);
  const savings = originalTotal * 0.2;
  const discountedTotal = originalTotal - savings;
  const handleAddToCart = () => {
    toast.success('Bundle added to cart successfully!', {
      description: `You saved SAR ${savings.toFixed(2)} on this bundle.`,
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-6493" data-mp="r9obz40qda6i" />
    });
    setStep(1);
    setSelectedGoal(null);
    setSelectedSupplements([]);
  };
  return <HelmetProvider data-id="element-6494" data-mp="w7zh51g2ywie">
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-6495" data-mp="w6oi1exkycxm">
        <SEO title="Bundle Builder | Innova Pharmacy" description="Build your custom supplement bundle and save 20%." data-id="element-6496" data-mp="116kjp7it1sa0" />

        <div className="hidden md:block" data-id="element-6497" data-mp="1a05ygquyz9jf">
          <AnnouncementBar data-id="element-6498" data-mp="19yvytx8gxtao" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6499" data-mp="1a2a2d8svc8jp" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6500" data-mp="1jxk21pvso3ub" />
        </div>

        <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-6501" data-mp="3b0kbtc69fjcj">
          <MobileInternalHeader title="Bundle Builder" data-id="element-6502" data-mp="3ps30rzh3ko81" />
        </div>

        <div className="pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-6503" data-mp="3l02y3z9n7h7r">
          <PageHeroBreadcrumb title="Bundle Builder" description="Build your custom supplement bundle and save 20%." breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Bundle Builder'
        }]} data-id="element-6504" data-mp="2mli6h6rga27" />
        </div>

        <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl" data-id="element-6505" data-mp="7mhl0d3q1vg6">
          {/* Progress Bar */}
          <div className="mb-8" data-id="element-6506" data-mp="2ee8o7shhnyx2">
            <div className="flex justify-between mb-2" data-id="element-6507" data-mp="29dbabs3ihr1d">
              <span className={`text-sm font-medium ${step >= 1 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6508" data-mp="29ew1d7qmaruh">
                1. Goal
              </span>
              <span className={`text-sm font-medium ${step >= 2 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6509" data-mp="tssioser1l3c">
                2. Supplements
              </span>
              <span className={`text-sm font-medium ${step >= 3 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6510" data-mp="k38xvh7h7h5u">
                3. Review
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-6511" data-mp="b4nke0gv2sbx">
              <motion.div className="h-full bg-[#1E2B55]" initial={{
              width: '33%'
            }} animate={{
              width: `${step / 3 * 100}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-6512" data-mp="3sc9362q2c6mh" />
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-6513" data-mp="ja42sxczwke9">
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-6514" data-mp="j9arrk7078qb">
                <div className="text-center mb-8" data-id="element-6515" data-mp="3vpeftg4rzioc">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-6516" data-mp="3rouj2do46h2q">
                    Choose Your Goal
                  </h2>
                  <p className="text-gray-600" data-id="element-6517" data-mp="3msx4ocwevksl">
                    Select your primary fitness goal to see recommended
                    supplements.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6518" data-mp="1kxd86siv6wup">
                  {goals.map(goal => <button key={goal.id} onClick={() => {
                setSelectedGoal(goal.id);
                setStep(2);
              }} className={`p-6 rounded-2xl border-2 text-left transition-all duration-200 ${selectedGoal === goal.id ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-200 hover:shadow-md'}`} data-id="element-6519" data-mp="1njvsj8xl8pm7">
                      <div className="text-4xl mb-4" data-id="element-6520" data-mp="14mkvluzfi89x">{goal.emoji}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-6521" data-mp="19jbbah7bn1ut">
                        {goal.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-6522" data-mp="1ehwd2l281tld">{goal.description}</p>
                    </button>)}
                </div>
              </motion.div>}

            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6 pb-32" data-id="element-6523" data-mp="2q0p8ilzz4ain">
                <div className="flex items-center justify-between mb-6" data-id="element-6524" data-mp="1u8cw062tarh1">
                  <button onClick={() => setStep(1)} className="flex items-center text-gray-600 hover:text-[#1E2B55]" data-id="element-6525" data-mp="1uagzimwstogs">
                    <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-6526" data-mp="11k7cfmnhv8is" />
                    Back
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6527" data-mp="16hgzivnev83g">
                    Select Supplements
                  </h2>
                  <div className="w-20" data-id="element-6528" data-mp="2hxxg4sapburw"></div> {/* Spacer for centering */}
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3" data-id="element-6529" data-mp="2mx1qhm9d3yjt">
                  <div className="bg-blue-100 p-2 rounded-full text-blue-600 mt-0.5" data-id="element-6530" data-mp="b9ez9kd53rqn">
                    <ZapIcon className="w-5 h-5" data-id="element-6531" data-mp="6alkzpq13wpf" />
                  </div>
                  <div data-id="element-6532" data-mp="6comb5wmtc9g">
                    <h4 className="font-semibold text-blue-900" data-id="element-6533" data-mp="1jvf8bdoxru7">
                      Bundle & Save 20%
                    </h4>
                    <p className="text-sm text-blue-700" data-id="element-6534" data-mp="3sqy611pm9w3w">
                      Select at least 2 supplements to unlock your bundle
                      discount.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6535" data-mp="3jo6teyl08uao">
                  {filteredSupplements.map(supp => {
                const isSelected = selectedSupplements.includes(supp.id);
                return <div key={supp.id} onClick={() => toggleSupplement(supp.id)} className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 flex gap-4 ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-200'}`} data-id="element-6536" data-mp="1cys9q0sn998y">
                        <div className="w-20 h-20 bg-white rounded-lg border border-gray-100 p-2 flex-shrink-0" data-id="element-6537" data-mp="378pcad0ms4oh">
                          <img src={supp.image} alt={supp.name} className="w-full h-full object-contain" data-id="element-6538" data-mp="2xh2ufxtsf7cr" />
                        </div>
                        <div className="flex-grow" data-id="element-6539" data-mp="3q4fgm6w1dd5j">
                          <h3 className="font-bold text-gray-900 mb-1 leading-tight" data-id="element-6540" data-mp="3c5z6e5yyt4in">
                            {supp.name}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2 line-clamp-2" data-id="element-6541" data-mp="3g5zmrw5mjpsc">
                            {supp.description}
                          </p>
                          <div className="font-bold text-[#1E2B55]" data-id="element-6542" data-mp="24sx7r5j6b8aa">
                            SAR {supp.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="absolute top-4 right-4" data-id="element-6543" data-mp="1sa1tpr8wn1o1">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'}`} data-id="element-6544" data-mp="1sbw5og277jfz">
                            {isSelected && <CheckIcon className="w-4 h-4 text-white" data-id="element-6545" data-mp="1eiwq6e2f1p8f" />}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Floating Summary Bar */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] p-4 z-40" data-id="element-6546" data-mp="1jfwxesb04y14">
                  <div className="container mx-auto max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-6547" data-mp="qt2ew0xqw8qa">
                    <div className="flex-grow flex items-center justify-between sm:justify-start sm:gap-8 w-full sm:w-auto" data-id="element-6548" data-mp="3q7miqpqawx23">
                      <div data-id="element-6549" data-mp="3u8y6sqtj1a86">
                        <div className="text-sm text-gray-500" data-id="element-6550" data-mp="3u62993llcnk9">
                          Selected Items
                        </div>
                        <div className="font-bold text-lg" data-id="element-6551" data-mp="7t3rr36el3et">
                          {selectedSupplements.length}
                        </div>
                      </div>
                      {selectedSupplements.length >= 2 && <>
                          <div data-id="element-6552" data-mp="hsuozcmclwil">
                            <div className="text-sm text-gray-500" data-id="element-6553" data-mp="ls3da1ymd26r">
                              Original
                            </div>
                            <div className="font-medium text-gray-400 line-through" data-id="element-6554" data-mp="qqwvg7ouphd9">
                              SAR {originalTotal.toFixed(2)}
                            </div>
                          </div>
                          <div data-id="element-6555" data-mp="2sl5kni9quffa">
                            <div className="text-sm text-green-600 font-medium" data-id="element-6556" data-mp="2kza6fx5qefbp">
                              Bundle Total
                            </div>
                            <div className="font-bold text-xl text-green-600" data-id="element-6557" data-mp="2g7io29wqbfwn">
                              SAR {discountedTotal.toFixed(2)}
                            </div>
                          </div>
                        </>}
                    </div>
                    <button onClick={() => setStep(3)} disabled={selectedSupplements.length < 2} className={`w-full sm:w-auto px-8 py-3 rounded-xl font-bold flex items-center justify-center transition-colors ${selectedSupplements.length >= 2 ? 'bg-[#1E2B55] text-white hover:bg-blue-900' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-6558" data-mp="33x34al1hzz28">
                      Continue to Review
                      <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-6559" data-mp="1eiybs624bfkv" />
                    </button>
                  </div>
                </div>
              </motion.div>}

            {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-6560" data-mp="1kzztngyu2xhi">
                <div className="flex items-center justify-between mb-6" data-id="element-6561" data-mp="121xavjwvpws5">
                  <button onClick={() => setStep(2)} className="flex items-center text-gray-600 hover:text-[#1E2B55]" data-id="element-6562" data-mp="nchp6ic7ghjb">
                    <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-6563" data-mp="eb9ra1dlpipr" />
                    Back
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6564" data-mp="icmyaesfjry9">
                    Review Bundle
                  </h2>
                  <div className="w-20" data-id="element-6565" data-mp="3vv07dg5piqya"></div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-6566" data-mp="3qv5fb9a73vbn">
                  <div className="p-6 border-b border-gray-100" data-id="element-6567" data-mp="8p95su8qt5w9">
                    <h3 className="font-bold text-lg mb-4" data-id="element-6568" data-mp="8qke97d4v1tp">
                      Selected Supplements
                    </h3>
                    <div className="space-y-4" data-id="element-6569" data-mp="inglzav7jjx7">
                      {selectedSupplements.map(id => {
                    const supp = mockSupplements.find(s => s.id === id);
                    if (!supp) return null;
                    const discountedPrice = supp.price * 0.8;
                    return <div key={supp.id} className="flex items-center gap-4" data-id="element-6570" data-mp="ndx3p7629rpd">
                            <div className="w-16 h-16 bg-gray-50 rounded-lg border border-gray-100 p-1 flex-shrink-0" data-id="element-6571" data-mp="wev8vss8k59j">
                              <img src={supp.image} alt={supp.name} className="w-full h-full object-contain" data-id="element-6572" data-mp="23ru20ln0hct8" />
                            </div>
                            <div className="flex-grow" data-id="element-6573" data-mp="2cq62rghldqze">
                              <h4 className="font-semibold text-gray-900" data-id="element-6574" data-mp="2hpr23s0oc1cn">
                                {supp.name}
                              </h4>
                            </div>
                            <div className="text-right" data-id="element-6575" data-mp="1p3g1ihd2i0fu">
                              <div className="text-sm text-gray-400 line-through" data-id="element-6576" data-mp="1u1himwqxnpoo">
                                SAR {supp.price.toFixed(2)}
                              </div>
                              <div className="font-bold text-green-600" data-id="element-6577" data-mp="1yuandkmeiy0r">
                                SAR {discountedPrice.toFixed(2)}
                              </div>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50" data-id="element-6578" data-mp="ttkm12e4arjf">
                    <div className="space-y-3 mb-6" data-id="element-6579" data-mp="tu2nqejjtxyq">
                      <div className="flex justify-between text-gray-600" data-id="element-6580" data-mp="otoo9kw0jwcb">
                        <span data-id="element-6581" data-mp="jv549q0zcuml">Original Total</span>
                        <span data-id="element-6582" data-mp="jubtmdywhkz6">SAR {originalTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-medium" data-id="element-6583" data-mp="fubf6tbsx9s4">
                        <span data-id="element-6584" data-mp="b1s1zxh10gnu">Bundle Savings (20%)</span>
                        <span data-id="element-6585" data-mp="b0yugua8tn4n">- SAR {savings.toFixed(2)}</span>
                      </div>
                      <div className="h-px bg-gray-200 my-2" data-id="element-6586" data-mp="61m25u0lrnuo"></div>
                      <div className="flex justify-between text-xl font-bold text-gray-900" data-id="element-6587" data-mp="15ydrgty2yq8">
                        <span data-id="element-6588" data-mp="3s9n4x6old7w6">Final Total</span>
                        <span data-id="element-6589" data-mp="3o9bltybvnfmt">SAR {discountedTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    <button onClick={handleAddToCart} className="w-full bg-[#1E2B55] text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-colors flex items-center justify-center" data-id="element-6590" data-mp="2d6euobep5iwr">
                      <ShoppingCartIcon className="w-5 h-5 mr-2" data-id="element-6591" data-mp="2471k1feu6oag" />
                      Add Bundle to Cart
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </main>

        <Footer data-id="element-6592" data-mp="1pfixszvdvxin" />
        <MobileFooterNav data-id="element-6593" data-mp="1pezog7jsuhg8" />
        <PharmacistChat data-id="element-6594" data-mp="1kh7m0jlj3cxs" />

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={loc => {
        setSelectedLocation(loc);
        setLocationModalOpen(false);
      }} data-id="element-6595" data-mp="1kgpq5o2s30qe" />
      </div>
    </HelmetProvider>;
}